import React, { useContext } from 'react'
import { OffreModel } from '../types/OffreModel'
import { FormattedMessage } from 'react-intl'
import { chauffageEtDiagnosticsEnum, diagnosticsTypeEnum } from '../enums/chauffageEtDiagnosticsEnum'
import { objectGetValues } from '../../../utils/ieCompatibility'
import styles from '../../../styles'
import { WHITE } from '../../../theme'
import { ResponsiveContext } from '../../../utils/context/ResponsiveContext'
import { makeStyles } from 'tss-react/mui'
import { Grid } from '@mui/material'

const useStyles = makeStyles<{ isLargeScreen: boolean }>()(
	(theme, { isLargeScreen }) => ({
		chauffageEtDiagnosticsContainer: {
			marginTop: isLargeScreen ? 73 : 44
		},
		titre: {
			...styles(theme).title0,
			fontSize: isLargeScreen ? 35 : 22,
			lineHeight: '55px',
			margin: 0
		},
		sousTitre: {
			...styles(theme).text29,
			fontSize: isLargeScreen ? 15 : 13,
			lineHeight: '25px',
			margin: 0,
			marginTop: isLargeScreen ? 30 : 21,
			marginBottom: isLargeScreen ? 13 : 15
		},
		echellePictoContainer: {
			height: isLargeScreen ? 140 : 134,
			padding: isLargeScreen ? '19px 35px 13px' : '19px 15px 20px',
			border: '1px solid #EFF3F5',
			borderRadius: 30,
			marginBottom: isLargeScreen ? 0 : 13,
			justifyContent: 'center'
		},
		pictoContainer: {
			padding: '2px !important',

			'& > div': {
				paddingTop: 7,
				paddingBottom: 7,
				height: 'fit-content',
				width: '100%',
				textAlign: 'center'
			},
			'& .consommationEnergetiqueBilan': {
				'&-0': {
					borderRadius: '30px 0 0 30px',
					backgroundColor: '#49A378'
				},
				'&-1': {
					backgroundColor: '#54B860'
				},
				'&-2': {
					backgroundColor: '#6DC281'
				},
				'&-3': {
					backgroundColor: '#FAEB4D'
				},
				'&-4': {
					backgroundColor: '#F5BF41'
				},
				'&-5': {
					backgroundColor: '#EF8D46'
				},
				'&-6': {
					borderRadius: '0 30px 30px 0',
					backgroundColor: '#D72E28'
				}
			},
			'& .emissionGazSerreBilan': {
				'&-0': {
					borderRadius: '30px 0 0 30px',
					backgroundColor: '#AED9F9'
				},
				'&-1': {
					backgroundColor: '#92B4CE'
				},
				'&-2': {
					backgroundColor: '#7C91AE'
				},
				'&-3': {
					backgroundColor: '#626E89'
				},
				'&-4': {
					backgroundColor: '#4E516F'
				},
				'&-5': {
					backgroundColor: '#37344E'
				},
				'&-6': {
					borderRadius: '0 30px 30px 0',
					backgroundColor: '#271C32'
				}
			},
			'& .bilan-selectionne': {
				borderRadius: 45,
				paddingTop: 12,
				paddingBottom: 11,
				...styles(theme).text7,
				fontSize: isLargeScreen ? 16 : 20,
				color: WHITE,
				lineHeight: '27px',

				'& span': {
					position: 'relative'
				}
			}
		},
		valeurBilan: {
			position: 'absolute',
			bottom: isLargeScreen ? -70 : -60,
			left: '50%',
			transform: 'translateX(-50%)',
			width: 141,
			...styles(theme).text2,
			fontSize: isLargeScreen ? 14 : 12,
			lineHeight: isLargeScreen ? '20px' : '15px',

			'& span': {
				...styles(theme).text0,
				fontSize: isLargeScreen ? 20 : 18,
				lineHeight: isLargeScreen ? '20px' : '15px'
			}
		},
		dernierDiagnostic: {
			...styles(theme).text3,
			fontSize: isLargeScreen ? 12 : 11,
			lineHeight: isLargeScreen ? '18px' : '16px',
			margin: 0,
			marginTop: isLargeScreen ? 18 : 21
		}
	})
)

interface AcceptingProps {
	offre: OffreModel
}

type ChauffageEtDiagnosticsProps = AcceptingProps

const ChauffageEtDiagnostics: React.FC<ChauffageEtDiagnosticsProps> = (
	{
		offre
	}
) => {
	const { isLargeScreen } = useContext(ResponsiveContext)
	const { classes, cx } = useStyles({ isLargeScreen })

	const EchelleDePicto = (type: diagnosticsTypeEnum) => (
		<Grid container item xs={12} alignItems="center" spacing={1}>
			{
				objectGetValues(chauffageEtDiagnosticsEnum).map(
					(bilanDiagnostic, index) => (
						<Grid
							container
							key={`${type}-${bilanDiagnostic}-${index}`}
							item
							xs
							className={classes.pictoContainer}
						>
							{
								bilanDiagnostic === offre.chauffageEtDiagnostics[type] ? (
									<div
										className={
											cx(
												`${type}-${index}`,
												'bilan-selectionne'
											)
										}
									>
										<span>
											{bilanDiagnostic}

											<div className={classes.valeurBilan}>
												<FormattedMessage
													id={`offre.chauffageEtDiagnostics.${type}.valeur`}
													defaultMessage="<span>{valeur}</span>{br} kWh/m2 an"
													description="Détail du bilan"
													values={{
														span: (chunk) => <span>{chunk}</span>,
														valeur: type === diagnosticsTypeEnum.consommationEnergetiqueBilan ? offre.chauffageEtDiagnostics.consommationEnergetiqueValeur : offre.chauffageEtDiagnostics.emissionGazSerreValeur,
														br: <br />
													}}
												/>
											</div>
										</span>
									</div>
								) : (
									<div className={`${type}-${index}`}></div>
								)
							}
						</Grid>
					)
				)
			}
		</Grid>
	)

	return (
		<Grid container item xs={12} className={classes.chauffageEtDiagnosticsContainer}>
			<Grid container item xs={12}>
				<h2 className={classes.titre}>
					<FormattedMessage
						id="offre.chauffageEtDiagnostics.titre"
						defaultMessage="Chauffage et diagnostics"
						description="Titre du bloc de chauffage et diagnostics"
					/>
				</h2>
			</Grid>

			{/* Bloc sur le diagnostic de performance (DPE) || Gaz à effet de serre (GES) */}
			<Grid container item xs={12} spacing={isLargeScreen ? 4 : 0}>
				{
					objectGetValues(diagnosticsTypeEnum).map(
						(diagnosticType) => (
							<Grid container item xs={12} md={6} key={diagnosticType}>
								<p className={classes.sousTitre}>
									<FormattedMessage
										id={`offre.chauffageEtDiagnostics.${diagnosticType}.titre`}
										defaultMessage="Diagnostic de performance énergétique (DPE)"
										description={`Titre du bloc ${diagnosticType}`}
									/>
								</p>

								<Grid container item xs={12} alignItems="flex-start" className={classes.echellePictoContainer}>
									{EchelleDePicto(diagnosticType)}
								</Grid>
							</Grid>
						)
					)
				}
			</Grid>
		</Grid>
	)
}

export default ChauffageEtDiagnostics